import { Map, LngLat } from 'maplibre-gl';
import type { Point, PointLike } from 'maplibre-gl';


interface InputObject {
	source: string;
	[key: string]: any;
}


interface OutputObject extends InputObject {
	clicked?: {
		lng: number;
		lat: number;
	};
	expanded: boolean;
	uniqueId: string;
}


/**
	* Creates a bounding box around a clicked point for feature querying
	* @param map - The MapLibre map instance
	* @param pixelRadius - Radius in pixels around the clicked point
	* @param clickedPoint - The geographic coordinates of the clicked point
	* @returns A bounding box as [PointLike, PointLike] for use with queryRenderedFeatures
	*/
export const expandClickedPoint = (
	map: Map,
	pixelRadius: number,
	clickedPoint: LngLat
): [PointLike, PointLike] => {
	const pap: Point = map.project(clickedPoint);
	return [
		[pap.x - pixelRadius, pap.y - pixelRadius], // Top-left corner
		[pap.x + pixelRadius, pap.y + pixelRadius], // Bottom-right corner
	];
};




export const filterEnergized = (geoJSONData: any, source: string): InputObject[] => {
	// Skip the last feature as requested and filter for points only
	const features = geoJSONData.findenergized.features
		//.slice(0, -1)
		.filter(feature => feature.geometry.type === 'Point');

	return features.map(feature => {
		// Extract coordinates from geometry
		const [lng, lat] = feature.geometry.coordinates;

		// Create a compatible object structure
		return {
			type: feature.type,
			properties: {
				...feature.properties,
				// Ensure iama is set for Point features (nodes)
				iama: "NODE"
			},
			source: source,
			layer: {
				// Point features are visualized as symbols
				type: 'symbol',
				id: `LVWR:${feature.properties.status}`,
				source: source,
				"source-layer": "nodes"
			},
			sourceLayer: "nodes",
			state: {}
		};
	});
}




// Process GeoJSON features for compatibility with wanted function
export const __filterEnergized = (geoJSONData: any, source: string): InputObject[] => {
	// Skip the last feature as requested
	const features = geoJSONData.findenergized.features.slice(0, -1);
	let type: string;
	return features.map(feature => {
		// Extract coordinates from geometry
		const [lng, lat] = feature.geometry.coordinates;
		if (feature.geometry.type === 'LineString') type = 'line'
		if (feature.geometry.type === 'Point') type = 'symbol'
		// Create a compatible object structure
		return {
			type: feature.type,
			properties: feature.properties,
			source: source,
			layer: {
				// Most GeoJSON point features would be visualized as symbols
				type: type,
				id: `LVWR:${feature.properties.status}`,
				source: source,
				"source-layer": "nodes"
			},
			sourceLayer: "nodes",
			state: {}
		};
	});
}

// Function to ensure all objects have source property set
export const ensureSource = (objects: any[], source: string): InputObject[] => {
	return objects.map(obj => ({
		...obj,
		source: obj.source || source,
		layer: {
			...obj.layer,
			source: obj.layer?.source || source
		}
	}));
}


/** takes stuff from queryRenderedFeatures ans removes duplicate lw_table_pkid **/

export const distilled = (
	objects: InputObject[],
	source: string,
	clicked?: { lng: number; lat: number; }
): OutputObject[] => {
	// First ensure all objects have source property
	const preparedObjects = ensureSource(objects, source);

	// Group objects by lw_id
	const uniqueById = {};
	let currentIndex = 0;

	// Filter only source-matching objects with appropriate layer types
	const filteredObjects = preparedObjects
		.filter(obj => obj.source === source)
		.filter(obj => obj.layer?.type && ['line', 'symbol'].includes(obj.layer.type));

	// Process each object and keep only the first occurrence of each lw_id
	filteredObjects.forEach(obj => {
		const id = obj.properties.lw_table_pkid;

		// If we haven't seen this lw_id yet, keep it
		if (!uniqueById[id]) {
			const { _vectorTileFeature, ...rest } = obj; // remove _vectorTileFeature if present

			uniqueById[id] = {
				...rest,
				clicked,
				expanded: true,
				uniqueId: `${id}-${currentIndex}-${Date.now()}`
			};

			currentIndex++;
		}
	});

	// Convert the object back to an array
	return Object.values(uniqueById);
};



export let setSourceOpacity = (map, sourceId, opacity) => {
	// Get the style object from the map
	const style = map.getStyle();

	// Filter layers to get only those from the specified source
	const targetLayers = style.layers.filter(layer => layer.source === sourceId);

	// Loop through each layer and update opacity
	targetLayers.forEach(layer => {
		// Different layer types have different opacity properties
		const layerType = layer.type;

		switch (layerType) {
			case 'fill':
				map.setPaintProperty(layer.id, 'fill-opacity', opacity);
				break;
			case 'line':
				map.setPaintProperty(layer.id, 'line-opacity', opacity);
				break;
			case 'circle':
				map.setPaintProperty(layer.id, 'circle-opacity', opacity);
				break;
			case 'symbol':
				map.setPaintProperty(layer.id, 'icon-opacity', opacity);
				map.setPaintProperty(layer.id, 'text-opacity', opacity);
				break;
			case 'raster':
				map.setPaintProperty(layer.id, 'raster-opacity', opacity);
				break;
			case 'fill-extrusion':
				map.setPaintProperty(layer.id, 'fill-extrusion-opacity', opacity);
				break;
			case 'heatmap':
				map.setPaintProperty(layer.id, 'heatmap-opacity', opacity);
				break;
			case 'hillshade':
				map.setPaintProperty(layer.id, 'hillshade-exaggeration', opacity);
				break;
			// Add other layer types as needed
		}
	});
}
